/**
 * Attribute Relationships - Creates HAS_ATTRIBUTE relationship structures
 *
 * Handles creation of attribute-based relationships for fields that are
 * stored as separate Attribute nodes in the graph database.
 *
 * @module queryBuilderCore/relationships/attributeRelationships
 */

import {
  LogicalOperator,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';

/**
 * Creates HAS_ATTRIBUTE relationships for attribute-based fields
 *
 * @param field - Attribute field name
 * @param operator - Filter operator
 * @param value - Filter value(s)
 * @param groupLogicalOperator - Logical operator for the filter group (AND/OR)
 * @param isNot - Whether to negate the filter
 * @returns Array of relationship structures for the attribute
 */
export function createAttributeRelationships(
  field: string,
  operator: string,
  value: string | string[] | number | number[] | boolean | boolean[],
  groupLogicalOperator: LogicalOperator,
  isNot = false
): Relationship[] {
  const relationships: Relationship[] = [];

  const stringValue = Array.isArray(value)
    ? value.map(v => String(v))
    : String(value);

  const shouldCreateSeparateRelationships =
    Array.isArray(stringValue) &&
    stringValue.length > 1 &&
    groupLogicalOperator === 'OR';

  if (shouldCreateSeparateRelationships && Array.isArray(stringValue)) {
    stringValue.forEach(val => {
      relationships.push({
        label: 'HAS_ATTRIBUTE',
        select_one: true,
        target: {
          labels: ['Attribute'],
          filters: [
            { field: 'name', operator: '=', value: field, not: false },
            {
              field: 'value',
              operator: '=',
              value: val,
              not: isNot,
            },
          ],
        },
      });
    });
  } else {
    let finalOperator = operator;
    if (operator === 'IN' || operator === 'NOT IN') {
      finalOperator = '=';
    }

    const relationship = {
      label: 'HAS_ATTRIBUTE',
      select_one: true,
      target: {
        labels: ['Attribute'],
        filters: [
          { field: 'name', operator: '=', value: field, not: false },
          {
            field: 'value',
            operator: finalOperator,
            value: stringValue,
            not: isNot,
          },
        ],
      },
    };

    relationships.push(relationship);
  }

  return relationships;
}
